<template>
  <div class="d-flex align-center row-gap-4 justify-space-between flex-wrap">
    <div class="d-flex align-center column-gap-4">
      <SearchTextField
        v-model="search"
        :disabled="isPristineChecklist"
        :tooltipText="searchTooltipText"
        @clear="updateSearchQuery('')"
      />
      <app-button
        data-locator="filters-button"
        :disabled="isPristineChecklist"
        @click="onOpenFiltersPane"
      >
        <template #left-icon>
          <app-icon-ds
            icon="sliders"
            size="xs"
          />
        </template>
        {{ $t('common.filters') }}
      </app-button>
    </div>
    <div class="d-flex flex-column-reverse flex-sm-row  align-start align-sm-center row-gap-4 column-gap-4">
      <app-segmented-control
        :active-option="activeViewModeOption"
        :disabled="isPristineChecklist"
        :options="viewModeOptions"
        class="view-mode-segmented-control"
        data-locator="view-mode-segmented-control"
        @change-active-option="onChangeActiveOption"
      />
      <div class="d-flex align-center column-gap-4">
        <app-dropdown-button
          data-locator="options-menu-button"
          :disabled="isPristineChecklist"
          type="outlined"
          variant="neutral"
        >
          {{ $t('project.checklist.ChecklistHeader.options') }}
          <template #list>
            <v-list data-locator="checklist-options-menu">
              <app-dropdown-button-item 
                data-locator="show-all-attachments-menu-item"
                icon="paperclip"
                :label="$t('project.checklist.ChecklistHeader.showAttachments')"
                @click="onShowAttachmentsClick"
              />

              <v-divider />

              <template v-if="isCurrentUserPm && isSubscriber">
                <app-dropdown-button-item 
                  data-locator="save-checklist-menu-item"
                  icon="save"
                  :label="$t('project.checklist.ChecklistHeader.saveChecklist')"
                  @click="onSaveChecklistClick"
                />
                
                <v-divider />
              </template>
              
              <AppDownloadFile
                :link="`${baseURL}/room/${roomMnemo}/downloads/todo-pdf`"
                class="d-block"
                forceReader
              >
                <template #default="{submitForm}">
                  <app-dropdown-button-item 
                    data-locator="pdf-export-menu-item"
                    icon="file-pdf"
                    :label="$t('project.checklist.ChecklistHeader.exportToPDF')"
                    @click="submitForm"
                  />
                </template>
              </AppDownloadFile>

              <AppDownloadFile :link="`${baseURL}/room/${roomMnemo}/downloads/todo-word`" class="d-block">
                <template #default="{submitForm}">
                  <app-dropdown-button-item 
                    data-locator="word-export-menu-item"
                    icon="file-word"
                    :label="$t('project.checklist.ChecklistHeader.exportToWord')"
                    @click="submitForm"
                  />
                </template>
              </AppDownloadFile>
              
              <app-dropdown-button-item 
                data-locator="share-by-mail-menu-item"
                icon="share"
                :label="$t('project.checklist.ChecklistHeader.shareByEmail')"
                @click="onSendChecklistByEmailClick"
              />
            </v-list>
          </template>
        </app-dropdown-button>

        <ChecklistAddButton v-if="isCurrentUserPm" data-locator="add-menu-button" />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import {
  DELETE_SEARCH_RESULTS,
  GET_SEARCH_RESULTS,
  OPEN_CHECKLIST_SHARE_BY_EMAIL_PANE,
  OPEN_SAVE_CHECKLIST_DIALOG,
} from '@/store/modules/checklist/action_types'
import { SET_FILTERS_PANE_IS_OPEN, SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN } from '@/store/modules/checklist/mutation_types'
import { UPDATE_PROFILE } from '@/store/modules/user/action_types'

import ChecklistAddButton from './ChecklistAddButton.vue'

const VIEW_MODE_OPTIONS = {
  DETAILED: 'detailed',
  COMPACT: 'compact',
}

const TRANSLATION_KEY_PREFIX = 'project.checklist.ChecklistHeader'

export default {
  name: 'ChecklistHeader',
  components: {
    AppDownloadFile,
    ChecklistAddButton,
    SearchTextField,
  },
  data () {
    return {
      activeViewModeOption: VIEW_MODE_OPTIONS.DETAILED,
      searchQuery: '',
    }
  },
  computed: {
    ...mapGetters('checklist', ['isPristineChecklist']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    ...mapGetters('user', ['isChecklistDetailed','isSubscriber']),
    baseURL () {
      return import.meta.env.VUE_APP_API_URL
    },
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.updateSearchQuery(val)
      }, 1000),
    },
    searchTooltipText () {
      return this.isPristineChecklist ? null : this.$t('project.checklist.ChecklistHeader.searchPlaceholder')
    },
    viewModeOptions () {
      return Object.values(VIEW_MODE_OPTIONS).map(option => {
        return {
          locatorValue: `${option}-view-mode-option`,
          name: this.$t(`${TRANSLATION_KEY_PREFIX}.viewModeOptionsLabels.${option}`),
          value: option,
        }
      })
    },
  },
  created () {
    this.activeViewModeOption = this.isChecklistDetailed ? VIEW_MODE_OPTIONS.DETAILED : VIEW_MODE_OPTIONS.COMPACT
  },
  methods: {
    ...mapActions('checklist', [
      DELETE_SEARCH_RESULTS,
      GET_SEARCH_RESULTS,
      OPEN_SAVE_CHECKLIST_DIALOG,
      OPEN_CHECKLIST_SHARE_BY_EMAIL_PANE,
    ]),
    ...mapActions('user', [UPDATE_PROFILE]),
    ...mapMutations('checklist', [SET_FILTERS_PANE_IS_OPEN, SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN]),
    onChangeActiveOption (option) {
      this.activeViewModeOption = option
      this.UPDATE_PROFILE({ isChecklistDetailed: option === VIEW_MODE_OPTIONS.DETAILED ? 1 : 0 })
    },
    onSaveChecklistClick () {
      this.OPEN_SAVE_CHECKLIST_DIALOG()
    },
    onShowAttachmentsClick () {
      this.SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN(true)
    },
    onSendChecklistByEmailClick () {
      this.OPEN_CHECKLIST_SHARE_BY_EMAIL_PANE()
    },
    onOpenFiltersPane () {
      this.SET_FILTERS_PANE_IS_OPEN(true)
    },
    searchChecklist () {
      this.search ? this.GET_SEARCH_RESULTS(this.searchQuery) : this.DELETE_SEARCH_RESULTS()
    },
    updateSearchQuery (searchQuery) {
      this.searchQuery = searchQuery
      this.searchChecklist()
    },
  },
}
</script>

<style scoped>
.view-mode-segmented-control {
  min-width: 315px;
}
</style>